import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import Select from 'react-select';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useLocationMaster } from '../../../hooks/useLocationMaster';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import { API_BASE_URL, API_URLS } from '../../../constants/api-urls';
import StorageService from '../../../services/StorageService';
import '../../../assets/styles/buyer_qa_request.scss';
import '../../../assets/styles/seller_profile.scss';

const { TabPane } = Tabs;

function pickFirstString(obj, keys) {
  if (!obj || typeof obj !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

function parseJsonSafe(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

// Initial state for Tab 1: SCOPE & LOGISTICS
const initialScopeLogistics = {
  product_name: '',
  inspection_category: '',
  inspection_type: [],
  keywords_tags: [],
  preferred_start_date: '',
  preferred_end_date: '',
  frequency: '',
  flexible_dates: false,
  flexible_days: 3,
  sampling_method: '',
  lot_quantity: '',
  acceptance_criteria: '',
  address_line_1: '',
  address_line_2: '',
  country: '',
  state: '',
  city: '',
  pincode: '',
  contact_name: '',
  designation: '',
  email: '',
  phone: '',
  security_ppe_requirements: '',
  detailed_inspection_instructions: '',
  technical_drawings_sops: [],
  industry_standards: '',
};

// Initial state for Tab 2: COMPLIANCE & COMMERCIALS
const initialComplianceCommercials = {
  active_qms: '',
  system_comments: '',
  compliance_history: '',
  insurance_policy_number: '',
  proof_of_insurance: [],
  identified_risks: false,
  risk_description: '',
  contingency_mitigation: '',
  budget_range: '',
  currency: '',
  bid_deadline: '',
  report_format: '',
  max_tat: '',
  reporting_notes: '',
};

export default function BuyerQARequest() {
  const navigate = useNavigate();
  useLocation(); // ensure component re-renders correctly on navigation in some routers
  const [searchParams] = useSearchParams();
  const quotationId = searchParams.get('quotation_id');
  const rfqId = searchParams.get('rfq_id');
  const companyId = searchParams.get('companyId');
  /** CM: coming from recruit-after-inspector — do not hydrate an old Draft from qa_requests; user is filing for the selected QA. */
  const fromRecruitParam = searchParams.get('fromRecruit');
  const freshParam = searchParams.get('fresh');
  const treatAsFreshCmIntent =
    Boolean(companyId) &&
    (fromRecruitParam === '1' ||
      String(fromRecruitParam).toLowerCase() === 'true' ||
      freshParam === '1');
  /** Contract manufacturing QA: external evaluator chosen on recruit page (stored in CM QC payload). */
  const [cmSelectedQualityInspectorId, setCmSelectedQualityInspectorId] = useState(null);

  const backHref = useMemo(() => {
    // If user came from CM quotes page, keep their companyId filter.
    if (companyId) return `/dashboard/buyer/contract-manufacturers/quotes?companyId=${companyId}`;
    // Default to the quotations list page used elsewhere in this flow.
    return '/seller-quotation-list';
  }, [companyId]);
  
  const [activeTab, setActiveTab] = useState('1');
  const [scopeLogistics, setScopeLogistics] = useState(initialScopeLogistics);
  const [complianceCommercials, setComplianceCommercials] = useState(initialComplianceCommercials);
  const [qaRequestId, setQaRequestId] = useState(null);
  /** From GET /qa/quotation/:id — when not `Draft`, the request was submitted and the form is read-only. */
  const [qaRequestStatus, setQaRequestStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const isQaFormReadOnly = qaRequestStatus != null && qaRequestStatus !== 'Draft';
  
  // File upload refs
  const technicalDrawingsRef = useRef(null);
  const proofOfInsuranceRef = useRef(null);
  /** When true, draft saves send full scope + compliance from UI (do not merge other tab from server). */
  const skipHydratingSavedDraftRef = useRef(false);
  
  // Get company name for file uploads
  const [companyName, setCompanyName] = useState('');
  
  // Category states
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  useEffect(() => {
    const user = StorageService.getUser();
    if (user && user.company_name) {
      setCompanyName(user.company_name);
    }
  }, []);
  
  // Upload file to backend and return URL
  const uploadFile = async (file, fileType = 'qa_document', category = 'company') => {
    try {
      const uploadCompanyName = companyName || 'QA_Request';
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('companyName', uploadCompanyName);
      formData.append('fileType', fileType);
      
      const response = await CommonService.post('/upload/single', formData);
      
      if (response && response.success && response.data) {
        return response.data.accessPath || response.data.filePath || '';
      }
      throw new Error(response?.message || 'Upload failed');
    } catch (error) {
      console.error('File upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errors?.[0] || error?.message || `Failed to upload ${fileType}`;
      toast.error(errorMessage);
      throw error;
    }
  };
  
  // Keyword input
  const [keywordInput, setKeywordInput] = useState('');
  
  // Master Data Hooks
  const {
    countries: rawCountries,
    states: rawStates,
    cities: rawCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  const { currencyOptions } = useCurrencyMaster();
  
  const countryOptions = rawCountries.map(c => ({ value: c.id, label: c.label }));
  const stateOptions = rawStates.map(s => ({ value: s.id, label: s.label }));
  const cityOptions = rawCities.map(c => ({ value: c.id, label: c.label }));
  
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);
  const [qualityManagement, setQualityManagement] = useState([]);
  const [loadingQualityManagement, setLoadingQualityManagement] = useState(false);

  // Fetch categories on component mount (same as buyer RFQ form)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await CommonService.get("/master/master-categories");
        if (response && response.success && response.data) {
          const categoriesList = Array.isArray(response.data) 
            ? response.data 
            : (response.data.categories || response.data.master_categories || []);
          setCategories(categoriesList);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  // Fetch inspection types from quality_inspector_service table
  useEffect(() => {
    const fetchInspectionTypes = async () => {
      try {
        setLoadingInspectionTypes(true);
        const response = await CommonService.get("/master/quality-inspector-services");
        if (response && response.success && response.data) {
          const servicesList = Array.isArray(response.data) 
            ? response.data 
            : (response.data.services || response.data.quality_inspector_services || []);
          setInspectionTypes(servicesList);
        }
      } catch (error) {
        console.error("Error fetching inspection types:", error);
      } finally {
        setLoadingInspectionTypes(false);
      }
    };
    fetchInspectionTypes();
  }, []);

  // Fetch quality management from quality_management table
  useEffect(() => {
    const fetchQualityManagement = async () => {
      try {
        setLoadingQualityManagement(true);
        const response = await CommonService.get("/master/quality-management");
        if (response && response.success && response.data) {
          const qmsList = Array.isArray(response.data) 
            ? response.data 
            : (response.data.quality_management || response.data.quality_management_systems || []);
          setQualityManagement(qmsList);
        }
      } catch (error) {
        console.error("Error fetching quality management:", error);
      } finally {
        setLoadingQualityManagement(false);
      }
    };
    fetchQualityManagement();
  }, []);
  
  // Map categories to react-select format (same as buyer RFQ form)
  const inspectionCategoryOptions = categories.map(cat => {
    const categoryId = cat.category_id || cat.id;
    const categoryName = cat.category || cat.category_name || cat.name;
    return { value: String(categoryId), label: categoryName };
  });
  
  // Map inspection types from quality_inspector_service table to react-select format
  const inspectionTypeOptions = inspectionTypes.map(service => {
    const serviceId = service.quality_inspector_service_id || service.id;
    const serviceName = service.quality_inspector_service || service.name || service.label;
    return { value: String(serviceId), label: serviceName };
  });
  
  // Frequency Options
  const frequencyOptions = [
    { value: 'one-time', label: 'One-time' },
    { value: 'periodic', label: 'Periodic' },
    { value: 'ongoing', label: 'Ongoing' },
  ];
  
  // Sampling Method Options
  const samplingMethodOptions = [
    { value: 'AQL 2.5 / 4.0', label: 'AQL 2.5 / 4.0' },
    { value: 'AQL 1.5 / 2.5', label: 'AQL 1.5 / 2.5' },
    { value: 'AQL 4.0 / 6.5', label: 'AQL 4.0 / 6.5' },
    { value: '100% Inspection', label: '100% Inspection' },
    { value: 'Custom', label: 'Custom' },
  ];
  
  // Map quality management from quality_management table to react-select format
  const activeQMSOptions = qualityManagement.map(qms => {
    const qmsId = qms.quality_management_id || qms.id;
    const qmsName = qms.quality_management || qms.name || qms.label;
    return { value: String(qmsId), label: qmsName };
  });
  
  // Budget Range Options
  const budgetRangeOptions = [
    { value: '$1k-2.5k', label: '$1k-2.5k' },
    { value: '$2.5k-5k', label: '$2.5k-5k' },
    { value: '$5k-10k', label: '$5k-10k' },
    { value: '$10k-25k', label: '$10k-25k' },
    { value: '$25k+', label: '$25k+' },
  ];
  
  // Report Format Options
  const reportFormatOptions = [
    { value: 'Platform Data', label: 'Platform Data' },
    { value: 'PDF Report', label: 'PDF Report' },
    { value: 'Excel Report', label: 'Excel Report' },
    { value: 'Custom Format', label: 'Custom Format' },
  ];
  
  // Max TAT Options
  const maxTATOptions = [
    { value: '24h', label: '24h' },
    { value: '48h', label: '48h' },
    { value: '72h', label: '72h' },
    { value: '1 week', label: '1 week' },
    { value: 'Custom', label: 'Custom' },
  ];
  
  // Check if quotation_id and rfq_id are provided
  useEffect(() => {
    if (!quotationId || !rfqId) {
      toast.error('Missing quotation or RFQ information');
      navigate('/seller-quotation-list');
      return;
    }
    
    // Load quotation/RFQ data first to get product name and category, then load existing QA request
    // Wait for categories and inspection types to load first to ensure proper mapping
    const initializeData = async () => {
      // Wait a bit for categories and inspection types to load if they're still loading
      if (loadingCategories || loadingInspectionTypes) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      const cmPrefill = await loadQuotationData();
      await loadExistingQARequest(cmPrefill);
    };
    
    initializeData();
  }, [quotationId, rfqId, companyId, fromRecruitParam, freshParam, loadingCategories, loadingInspectionTypes]);

  // CM flow: load buyer-selected external QA id from QC requirements (must not rely on classic RFQ ids).
  useEffect(() => {
    if (!companyId || !rfqId) {
      setCmSelectedQualityInspectorId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cmRes = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_GET(rfqId));
        if (cancelled) return;
        const envelope = cmRes?.data;
        const qc = envelope?.qc ?? envelope;
        const rawPayload = qc?.qc_payload_json ?? qc?.qcPayloadJson;
        const payload = parseJsonSafe(rawPayload) || (rawPayload && typeof rawPayload === 'object' ? rawPayload : null);
        const sel = payload?.buyerExternalEvaluatorSelectedQualityInspectorId;
        const n = sel != null && String(sel).trim() !== '' ? Number(sel) : NaN;
        setCmSelectedQualityInspectorId(Number.isFinite(n) && n > 0 ? n : null);
      } catch (e) {
        if (!cancelled) {
          console.error('Error loading CM external QA selection:', e);
          setCmSelectedQualityInspectorId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, rfqId]);
  
  // Load quotation + RFQ (+ CM) data to auto-fill product name and inspection category
  const loadQuotationData = async () => {
    let productName = '';
    let inspectionCategoryId = '';

    // 1) Seller quotation line items (classic buyer–seller flow)
    try {
      const qRes = await CommonService.get(`/quotation/${quotationId}`);
      if (qRes && qRes.success && qRes.data) {
        const q = qRes.data;
        const items = Array.isArray(q.items) ? q.items : [];
        if (items.length > 0) {
          productName = pickFirstString(items[0], ['description', 'product_name', 'name', 'item_name']);
        }
        if (!productName) {
          productName = pickFirstString(q, ['product_name', 'title', 'rfq_title']);
        }
      }
    } catch (e) {
      console.error('Error fetching quotation for QA auto-fill:', e);
    }

    // 2) Classic RFQ (`rfq_requests`)
    try {
      const rfqResponse = await CommonService.get(`/rfq/${rfqId}`);
      if (rfqResponse && rfqResponse.success && rfqResponse.data) {
        const rfq = rfqResponse.data;
        if (!productName) {
          const items = Array.isArray(rfq.items) ? rfq.items : [];
          if (items.length > 0) {
            productName = pickFirstString(items[0], ['product_name', 'description', 'name']);
          } else {
            productName = pickFirstString(rfq, ['product_name', 'category_name']);
          }
        }
        if (rfq.category_id) {
          inspectionCategoryId = String(rfq.category_id);
          console.log('Auto-populating inspection category from RFQ category_id:', inspectionCategoryId);
        }
      }
    } catch (error) {
      console.error('Error fetching RFQ data for product name and category:', error);
    }

    // 3) Contract manufacturer: QC payload (productNameCode / template)
    if (!productName) {
      try {
        const cmRes = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.QC_REQUIREMENTS_GET(rfqId));
        const envelope = cmRes?.data;
        const qc = envelope?.qc ?? envelope;
        const rawPayload = qc?.qc_payload_json ?? qc?.qcPayloadJson;
        const payload = parseJsonSafe(rawPayload) || (rawPayload && typeof rawPayload === 'object' ? rawPayload : null);
        if (payload && typeof payload === 'object') {
          productName = pickFirstString(payload, ['productNameCode', 'product_name', 'templateName']);
        }
      } catch (e) {
        console.error('Error fetching CM QC requirements for QA auto-fill:', e);
      }
    }

    // 4) Contract manufacturer: buyer’s RFQ submissions list (payload_json.products[].partName)
    if (!productName) {
      try {
        const subRes = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, {
          limit: 200,
          offset: 0,
        });
        const inner = subRes?.data;
        const rows = Array.isArray(inner?.items) ? inner.items : [];
        const row = rows.find((r) => String(r?.id) === String(rfqId));
        if (row) {
          const payload = parseJsonSafe(row.payload_json) || row.payload_json;
          if (payload && typeof payload === 'object') {
            const products = payload.products;
            if (Array.isArray(products) && products[0]) {
              productName = pickFirstString(products[0], ['partName', 'part_name', 'name', 'description']);
            }
          }
        }
      } catch (e) {
        console.error('Error fetching CM RFQ submissions for QA auto-fill:', e);
      }
    }

    if (productName || inspectionCategoryId) {
      setScopeLogistics((prev) => ({
        ...prev,
        ...(productName && { product_name: productName }),
        ...(inspectionCategoryId && { inspection_category: inspectionCategoryId }),
      }));
    }
    return { productName, inspectionCategoryId };
  };
  
  // Load existing QA request
  const loadExistingQARequest = async (cmPrefill = { productName: '', inspectionCategoryId: '' }) => {
    try {
      setIsLoadingData(true);
      let response;
      try {
        response = await CommonService.get(
          `/qa/quotation/${quotationId}`,
          companyId && rfqId ? { rfq_id: rfqId } : {}
        );
      } catch (e) {
        response = { success: false, data: null };
      }
      // CM: URL quotation_id may not match qa_requests.quotation_id; resolve by rfq_id.
      if (companyId && rfqId && (!response?.success || response?.data == null)) {
        try {
          response = await CommonService.get(`/qa/quotation/rfq/${rfqId}`);
        } catch (e2) {
          response = { success: false, data: null };
        }
      }

      if (response && response.success && response.data) {
        const data = response.data;
        const statusStr = data.status != null ? String(data.status) : null;
        const isDraftLike = !statusStr || statusStr === 'Draft';
        const skipSavedDraftPayload =
          treatAsFreshCmIntent && isDraftLike;

        setQaRequestId(data.qa_request_id ?? null);
        setQaRequestStatus(statusStr);

        if (skipSavedDraftPayload) {
          skipHydratingSavedDraftRef.current = true;
          setComplianceCommercials({ ...initialComplianceCommercials });
          setScopeLogistics({
            ...initialScopeLogistics,
            ...(cmPrefill.productName && { product_name: cmPrefill.productName }),
            ...(cmPrefill.inspectionCategoryId && { inspection_category: cmPrefill.inspectionCategoryId }),
          });
          return;
        }

        skipHydratingSavedDraftRef.current = false;

        // Load Tab 1 data
        if (data.scope_logistics) {
          const scopeData = typeof data.scope_logistics === 'string' 
            ? JSON.parse(data.scope_logistics) 
            : data.scope_logistics;
          
          setScopeLogistics(prev => {
            // Preserve auto-populated product_name and inspection_category from RFQ if they exist
            // These are set by loadQuotationData() and should not be overwritten
            const preservedProductName = prev.product_name || '';
            const preservedInspectionCategory = prev.inspection_category || '';
            
            // Handle inspection_type - convert to array if it's a string (for backward compatibility)
            let inspectionTypeValue = scopeData.inspection_type || [];
            if (typeof inspectionTypeValue === 'string') {
              // If it's a string, try to parse it or convert to array
              try {
                inspectionTypeValue = JSON.parse(inspectionTypeValue);
              } catch (e) {
                // If parsing fails, treat as single value and convert to array
                inspectionTypeValue = inspectionTypeValue ? [inspectionTypeValue] : [];
              }
            } else if (!Array.isArray(inspectionTypeValue)) {
              inspectionTypeValue = [];
            }
            
            // Convert country, state, city from strings to numbers for dropdown matching
            // (They're stored as strings in DB but dropdowns expect numbers)
            const countryId = scopeData.country && scopeData.country !== '' 
              ? (typeof scopeData.country === 'string' ? parseInt(scopeData.country, 10) : Number(scopeData.country))
              : '';
            const stateId = scopeData.state && scopeData.state !== '' 
              ? (typeof scopeData.state === 'string' ? parseInt(scopeData.state, 10) : Number(scopeData.state))
              : '';
            const cityId = scopeData.city && scopeData.city !== '' 
              ? (typeof scopeData.city === 'string' ? parseInt(scopeData.city, 10) : Number(scopeData.city))
              : '';
            
            return {
              ...prev,
              ...scopeData,
              // Keep auto-populated product_name from RFQ if it exists, otherwise use saved value
              product_name: preservedProductName || scopeData.product_name || prev.product_name,
              // Keep auto-populated inspection_category from RFQ if it exists, otherwise use saved value
              inspection_category: preservedInspectionCategory || scopeData.inspection_category || prev.inspection_category,
              inspection_type: inspectionTypeValue,
              keywords_tags: scopeData.keywords_tags || [],
              technical_drawings_sops: scopeData.technical_drawings_sops || [],
              // Convert location IDs to numbers for dropdown matching
              country: countryId,
              state: stateId,
              city: cityId,
            };
          });
          
          // Update location lists after state is set (use setTimeout to ensure state update completes)
          if (scopeData.country && scopeData.country !== '') {
            const countryId = typeof scopeData.country === 'string' ? parseInt(scopeData.country, 10) : Number(scopeData.country);
            if (!isNaN(countryId)) {
              // Use setTimeout to ensure state update completes before updating location lists
              setTimeout(() => {
                updateListsForCountry(countryId);
                if (scopeData.state && scopeData.state !== '') {
                  const stateId = typeof scopeData.state === 'string' ? parseInt(scopeData.state, 10) : Number(scopeData.state);
                  if (!isNaN(stateId)) {
                    setTimeout(() => {
                      updateListsForState(countryId, stateId);
                    }, 100);
                  }
                }
              }, 100);
            }
          }
        }
        
        // Load Tab 2 data
        if (data.compliance_commercials) {
          const complianceData = typeof data.compliance_commercials === 'string' 
            ? JSON.parse(data.compliance_commercials) 
            : data.compliance_commercials;
          
          setComplianceCommercials(prev => ({
            ...prev,
            ...complianceData,
            proof_of_insurance: complianceData.proof_of_insurance || [],
          }));
        }
      } else {
        setQaRequestId(null);
        setQaRequestStatus(null);
        skipHydratingSavedDraftRef.current = Boolean(companyId && treatAsFreshCmIntent);
      }
    } catch (error) {
      console.error('Error loading QA request:', error);
      setQaRequestStatus(null);
      skipHydratingSavedDraftRef.current = false;
      // If no existing request, that's fine - we'll create a new one
    } finally {
      setIsLoadingData(false);
    }
  };
  
  // Handle keyword input
  const handleKeywordInput = (e) => {
    setKeywordInput(e.target.value);
  };
  
  const addKeyword = () => {
    const v = keywordInput.trim();
    if (!v) return;
    if (!scopeLogistics.keywords_tags.includes(v)) {
      setScopeLogistics(prev => ({
        ...prev,
        keywords_tags: [...prev.keywords_tags, v]
      }));
    }
    setKeywordInput('');
  };
  
  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };
  
  const removeKeyword = (keyword) => {
    setScopeLogistics(prev => ({
      ...prev,
      keywords_tags: prev.keywords_tags.filter(k => k !== keyword)
    }));
  };
  
  // Handle file uploads
  const handleTechnicalDrawingsUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    try {
      setLoading(true);
      const uploadedUrls = [];
      
      for (const file of files) {
        const url = await uploadFile(file);
        if (url) {
          uploadedUrls.push(url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        setScopeLogistics(prev => ({
          ...prev,
          technical_drawings_sops: [...prev.technical_drawings_sops, ...uploadedUrls]
        }));
        toast.success(`${uploadedUrls.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };
  
  const handleProofOfInsuranceUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    try {
      setLoading(true);
      const uploadedUrls = [];
      
      for (const file of files) {
        const url = await uploadFile(file);
        if (url) {
          uploadedUrls.push(url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        setComplianceCommercials(prev => ({
          ...prev,
          proof_of_insurance: [...prev.proof_of_insurance, ...uploadedUrls]
        }));
        toast.success(`${uploadedUrls.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to prepare scope_logistics for API
  // Note: inspection_type must be a string, but keywords_tags and technical_drawings_sops must be arrays
  const prepareScopeLogistics = (scopeData) => {
    if (!scopeData || Object.keys(scopeData).length === 0) {
      return {}; // Return empty object if no data
    }
    
    const prepared = { ...scopeData };
    
    // Convert inspection_category to string (validator expects string, but dropdown returns number/ID)
    if (prepared.inspection_category !== undefined && prepared.inspection_category !== null && prepared.inspection_category !== '') {
      prepared.inspection_category = String(prepared.inspection_category);
    } else if (prepared.inspection_category === undefined || prepared.inspection_category === null) {
      prepared.inspection_category = '';
    }
    
    // Convert inspection_type array to JSON string (validator expects string)
    if (Array.isArray(prepared.inspection_type)) {
      prepared.inspection_type = JSON.stringify(prepared.inspection_type);
    } else if (prepared.inspection_type === undefined || prepared.inspection_type === null) {
      prepared.inspection_type = '';
    }
    // Ensure keywords_tags and technical_drawings_sops are arrays (validator expects arrays)
    if (!Array.isArray(prepared.keywords_tags)) {
      prepared.keywords_tags = [];
    }
    if (!Array.isArray(prepared.technical_drawings_sops)) {
      prepared.technical_drawings_sops = [];
    }
    // Ensure email is either a valid email string or empty string (validator allows empty string)
    if (prepared.email === undefined || prepared.email === null) {
      // Don't include email field if undefined/null (optional field)
      delete prepared.email;
    } else if (prepared.email === '') {
      // Keep empty string as is (validator allows it)
      prepared.email = '';
    }
    // If email has a value, it will be validated by Joi.email()
    
    // Convert country, state, city to strings (validator expects strings, but dropdowns return numbers/IDs)
    if (prepared.country !== undefined && prepared.country !== null && prepared.country !== '') {
      prepared.country = String(prepared.country);
    } else if (prepared.country === undefined || prepared.country === null) {
      prepared.country = '';
    }
    if (prepared.state !== undefined && prepared.state !== null && prepared.state !== '') {
      prepared.state = String(prepared.state);
    } else if (prepared.state === undefined || prepared.state === null) {
      prepared.state = '';
    }
    if (prepared.city !== undefined && prepared.city !== null && prepared.city !== '') {
      prepared.city = String(prepared.city);
    } else if (prepared.city === undefined || prepared.city === null) {
      prepared.city = '';
    }
    
    // Convert other numeric fields to strings (validator expects strings)
    if (prepared.lot_quantity !== undefined && prepared.lot_quantity !== null && prepared.lot_quantity !== '') {
      prepared.lot_quantity = String(prepared.lot_quantity);
    } else if (prepared.lot_quantity === undefined || prepared.lot_quantity === null) {
      prepared.lot_quantity = '';
    }
    if (prepared.pincode !== undefined && prepared.pincode !== null && prepared.pincode !== '') {
      prepared.pincode = String(prepared.pincode);
    } else if (prepared.pincode === undefined || prepared.pincode === null) {
      prepared.pincode = '';
    }
    if (prepared.phone !== undefined && prepared.phone !== null && prepared.phone !== '') {
      prepared.phone = String(prepared.phone);
    } else if (prepared.phone === undefined || prepared.phone === null) {
      prepared.phone = '';
    }
    
    return prepared;
  };
  
  // Helper function to prepare compliance_commercials for API
  // Note: proof_of_insurance must be an array (validator expects array)
  const prepareComplianceCommercials = (complianceData) => {
    if (!complianceData || Object.keys(complianceData).length === 0) {
      return {}; // Return empty object if no data
    }
    
    const prepared = { ...complianceData };
    // Ensure proof_of_insurance is an array (validator expects array)
    if (!Array.isArray(prepared.proof_of_insurance)) {
      prepared.proof_of_insurance = [];
    }
    return prepared;
  };
  
  // Save as Draft
  const handleSaveDraft = async (tab) => {
    if (isQaFormReadOnly) {
      toast.info('This QA request has already been submitted and cannot be edited.');
      return;
    }
    try {
      setSaving(true);
      
      // Validate quotation_id and rfq_id
      const parsedQuotationId = parseInt(quotationId);
      const parsedRfqId = parseInt(rfqId);
      
      if (isNaN(parsedQuotationId) || isNaN(parsedRfqId)) {
        toast.error('Invalid quotation or RFQ ID');
        setSaving(false);
        return;
      }
      
      // Build payload - different structure for create vs update
      let payload;
      
      // Always include both scope_logistics and compliance_commercials
      // to preserve data from the other tab when updating
      if (qaRequestId) {
        // For UPDATE: updateQuotationQARequestSchema doesn't allow quotation_id and rfq_id
        payload = {
          status: 'Draft',
        };
        if (skipHydratingSavedDraftRef.current) {
          payload.scope_logistics = prepareScopeLogistics(scopeLogistics);
          payload.compliance_commercials = prepareComplianceCommercials(complianceCommercials);
        } else {
          try {
            let existingResponse;
            try {
              existingResponse = await CommonService.get(
                `/qa/quotation/${quotationId}`,
                companyId && rfqId ? { rfq_id: rfqId } : {}
              );
            } catch (e) {
              existingResponse = { success: false, data: null };
            }
            if (companyId && rfqId && (!existingResponse?.success || existingResponse?.data == null)) {
              try {
                existingResponse = await CommonService.get(`/qa/quotation/rfq/${rfqId}`);
              } catch (e2) {
                existingResponse = { success: false, data: null };
              }
            }
            if (existingResponse && existingResponse.success && existingResponse.data) {
              const existing = existingResponse.data;
              // Preserve existing data from the other tab
              if (tab === '1') {
                // Saving Tab 1 - preserve Tab 2 data
                payload.scope_logistics = prepareScopeLogistics(scopeLogistics);
                if (existing.compliance_commercials) {
                  const existingCompliance = typeof existing.compliance_commercials === 'string' 
                    ? JSON.parse(existing.compliance_commercials) 
                    : existing.compliance_commercials;
                  // Convert back to proper format for API
                  payload.compliance_commercials = prepareComplianceCommercials(existingCompliance);
                } else {
                  payload.compliance_commercials = {}; // Empty object if no existing data
                }
              } else if (tab === '2') {
                // Saving Tab 2 - preserve Tab 1 data
                payload.compliance_commercials = prepareComplianceCommercials(complianceCommercials);
                if (existing.scope_logistics) {
                  const existingScope = typeof existing.scope_logistics === 'string' 
                    ? JSON.parse(existing.scope_logistics) 
                    : existing.scope_logistics;
                  // Convert back to proper format for API
                  payload.scope_logistics = prepareScopeLogistics(existingScope);
                } else {
                  payload.scope_logistics = {}; // Empty object if no existing data
                }
              }
            } else {
              // If fetch fails, send current tab's data and empty object for the other tab
              if (tab === '1') {
                payload.scope_logistics = prepareScopeLogistics(scopeLogistics);
                payload.compliance_commercials = {}; // Empty object for Tab 2
              } else if (tab === '2') {
                payload.compliance_commercials = prepareComplianceCommercials(complianceCommercials);
                payload.scope_logistics = {}; // Empty object for Tab 1
              }
            }
          } catch (fetchError) {
            console.error('Error fetching existing data:', fetchError);
            // If fetch fails, send current tab's data and empty object for the other tab
            if (tab === '1') {
              payload.scope_logistics = prepareScopeLogistics(scopeLogistics);
              payload.compliance_commercials = {}; // Empty object for Tab 2
            } else if (tab === '2') {
              payload.compliance_commercials = prepareComplianceCommercials(complianceCommercials);
              payload.scope_logistics = {}; // Empty object for Tab 1
            }
          }
        }
      } else {
        // For CREATE: createQuotationQARequestSchema requires quotation_id and rfq_id
        payload = {
          quotation_id: parsedQuotationId,
          rfq_id: parsedRfqId,
          status: 'Draft',
        };
        
        // Creating new - send current tab's data and empty object for the other tab
        if (tab === '1') {
          payload.scope_logistics = prepareScopeLogistics(scopeLogistics);
          payload.compliance_commercials = {}; // Empty object for Tab 2
        } else if (tab === '2') {
          payload.compliance_commercials = prepareComplianceCommercials(complianceCommercials);
          payload.scope_logistics = {}; // Empty object for Tab 1
        }
      }
      
      // Log payload for debugging
      console.log('Saving draft payload:', JSON.stringify(payload, null, 2));
      
      let response;
      if (qaRequestId) {
        // Update existing request using unified QA API
        response = await CommonService.put(`/qa/quotation/${qaRequestId}`, payload);
      } else {
        // Create new request using unified QA API
        response = await CommonService.post(`/qa/quotation`, payload);
        if (response && response.success && response.data) {
          setQaRequestId(response.data.qa_request_id);
        }
      }
      
      if (response && response.success) {
        toast.success(`Tab ${tab} saved as draft successfully`);
      } else {
        toast.error('Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error details:', error.response?.data?.errors || error.response?.data?.error || error.message);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save draft';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  
  // Broadcast RFQ
  const handleBroadcastRFQ = async () => {
    if (isQaFormReadOnly) {
      toast.info('This QA request has already been submitted.');
      return;
    }
    const isContractManufacturingQaFlow = Boolean(companyId);

    // Validate required fields
    if (!scopeLogistics.product_name || !scopeLogistics.inspection_category || 
        !scopeLogistics.inspection_type || (Array.isArray(scopeLogistics.inspection_type) && scopeLogistics.inspection_type.length === 0) || !scopeLogistics.preferred_start_date || 
        !scopeLogistics.preferred_end_date || !scopeLogistics.address_line_1 || 
        !scopeLogistics.country || !scopeLogistics.state || !scopeLogistics.city || 
        !scopeLogistics.pincode || !scopeLogistics.contact_name || !scopeLogistics.email) {
      toast.error('Please fill all required fields in Tab 1');
      setActiveTab('1');
      return;
    }
    
    if (!complianceCommercials.budget_range || !complianceCommercials.currency || 
        !complianceCommercials.bid_deadline || !complianceCommercials.report_format || 
        !complianceCommercials.max_tat) {
      toast.error('Please fill all required fields in Tab 2');
      setActiveTab('2');
      return;
    }

    if (isContractManufacturingQaFlow) {
      if (cmSelectedQualityInspectorId == null || !Number.isFinite(Number(cmSelectedQualityInspectorId))) {
        toast.error('No external QA selected. Open Recruit external QA and choose an inspector, then try again.');
        return;
      }
    }
    
    try {
      setSaving(true);
      
      let payload;
      let response;
      let finalQaRequestId = qaRequestId;

      const cmInspectorPayload =
        isContractManufacturingQaFlow && cmSelectedQualityInspectorId != null
          ? { selected_quality_inspector_id: Number(cmSelectedQualityInspectorId) }
          : {};
      
      if (qaRequestId) {
        // For UPDATE: updateQuotationQARequestSchema doesn't allow quotation_id and rfq_id
        payload = {
          scope_logistics: prepareScopeLogistics(scopeLogistics),
          compliance_commercials: prepareComplianceCommercials(complianceCommercials),
          status: 'Broadcast',
          ...cmInspectorPayload,
        };
        // Update existing request using unified QA API
        response = await CommonService.put(`/qa/quotation/${qaRequestId}`, payload);
        finalQaRequestId = qaRequestId;
      } else {
        // For CREATE: createQuotationQARequestSchema requires quotation_id and rfq_id
        payload = {
          quotation_id: parseInt(quotationId),
          rfq_id: parseInt(rfqId),
          scope_logistics: prepareScopeLogistics(scopeLogistics),
          compliance_commercials: prepareComplianceCommercials(complianceCommercials),
          status: 'Broadcast',
          ...cmInspectorPayload,
        };
        // Create new request using unified QA API
        response = await CommonService.post(`/qa/quotation`, payload);
        if (response && response.success && response.data) {
          finalQaRequestId = response.data.qa_request_id;
          setQaRequestId(finalQaRequestId);
        }
      }
      
      if (response && response.success) {
        if (isContractManufacturingQaFlow) {
          navigate(`/dashboard/buyer/contract-manufacturers/quotes?companyId=${encodeURIComponent(companyId)}`);
          return;
        }

        // Auction / buyer–seller: success page with location-based inspector matching summary
        const pincode = scopeLogistics.pincode || '';
        const cityId = scopeLogistics.city ? Number(scopeLogistics.city) : null;
        const stateId = scopeLogistics.state ? Number(scopeLogistics.state) : null;
        
        const cityName = cityId ? (cityOptions.find(opt => opt.value === cityId)?.label || '') : '';
        const stateName = stateId ? (stateOptions.find(opt => opt.value === stateId)?.label || '') : '';
        
        if (finalQaRequestId) {
          const queryParams = new URLSearchParams({
            qa_request_id: finalQaRequestId,
            rfq_id: rfqId,
            ...(pincode && { pincode }),
            ...(cityName && { city: cityName }),
            ...(stateName && { state: stateName })
          });
          navigate(`/quotation/${quotationId}/qa-request?${queryParams.toString()}`);
        } else {
          navigate('/seller-quotation-list');
        }
      } else {
        toast.error('Failed to submit QA request');
      }
    } catch (error) {
      console.error('Error broadcasting RFQ:', error);
      toast.error(error.response?.data?.message || 'Failed to broadcast QA request');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle country change
  const handleCountryChange = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : '';
    setScopeLogistics(prev => ({
      ...prev,
      country: countryId,
      state: '',
      city: '',
    }));
    if (countryId) {
      updateListsForCountry(countryId);
    }
  };
  
  // Handle state change
  const handleStateChange = (selectedOption) => {
    const stateId = selectedOption ? selectedOption.value : '';
    setScopeLogistics(prev => {
      const updated = {
        ...prev,
        state: stateId,
        city: '',
      };
      // Update cities list when state changes
      if (stateId && prev.country) {
        updateListsForState(prev.country, stateId);
      }
      return updated;
    });
  };
  
  if (isLoadingData) {
    return (
      <div className="bqa-loading">
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="seller-profile">
      <div className="container-fluid">
        <div className="sp-main">
          <div className="bqa-header">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => {
                // Prefer browser history back when possible; otherwise fall back to a safe page.
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate(backHref);
                }
              }}
            >
              ← Back
            </button>
          </div>
          <h1 className="sp-main-title">Quality Inspector Request</h1>
          <p className="sp-subtitle">Create a comprehensive QA request for quality inspection</p>
          {isQaFormReadOnly && qaRequestStatus ? (
            <div className="alert alert-info py-2 px-3 mb-3" role="status">
              This QA request has already been submitted (status: <strong>{qaRequestStatus}</strong>). Fields are read-only; use Back to return to your quotes.
            </div>
          ) : null}
          
          <div className="sp-card">
            <Tabs activeKey={activeTab} onChange={setActiveTab} className="sp-tabs">
              <TabPane tab="1. SCOPE & LOGISTICS" key="1">
                <fieldset
                  disabled={isQaFormReadOnly}
                  className="bqa-readonly-fieldset"
                  style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}
                >
                <div className="container-fluid">
                  {/* INSPECTION IDENTIFICATION */}
                  <section className="sp-section">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">INSPECTION IDENTIFICATION</h6>
                      <p className="text-muted small mb-2">Core project details</p>
                      
                      <div className="row g-2">
                        <div className="col-12 col-md-4 mb-2">
                          <input
                            type="text"
                            className="input-pill"
                            placeholder="PRODUCT NAME *"
                            value={scopeLogistics.product_name}
                            onChange={(e) => setScopeLogistics(prev => ({ ...prev, product_name: e.target.value }))}
                            // Auto-populated, but buyer can edit if needed.
                          />
                        </div>
                      
                        <div className="col-12 col-md-4 mb-2">
                          <Select
                            options={inspectionCategoryOptions}
                            value={inspectionCategoryOptions.find(opt => opt.value === scopeLogistics.inspection_category)}
                            onChange={(option) => setScopeLogistics(prev => ({ ...prev, inspection_category: option ? option.value : '' }))}
                            placeholder="INSPECTION CATEGORY"
                            isClearable
                            isLoading={loadingCategories}
                            isDisabled={isQaFormReadOnly}
                            className="input-pill-select"
                            classNamePrefix="react-select"
                          />
                        </div>
                      
                        <div className="col-12 col-md-4 mb-2">
                          <Select
                            options={inspectionTypeOptions}
                            value={inspectionTypeOptions.filter(opt => 
                              Array.isArray(scopeLogistics.inspection_type) 
                                ? scopeLogistics.inspection_type.includes(opt.value)
                                : scopeLogistics.inspection_type === opt.value
                            )}
                            onChange={(selectedOptions) => {
                              const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                              setScopeLogistics(prev => ({ ...prev, inspection_type: values }));
                            }}
                            placeholder="INSPECTION TYPE *"
                            isMulti
                            isClearable
                            isLoading={loadingInspectionTypes}
                            isDisabled={isQaFormReadOnly}
                            className="input-pill-select"
                            classNamePrefix="react-select"
                          />
                        </div>
                      
                      <div className="col-12 mb-2">
                        <div className="product-input-row d-flex gap-2">
                          <input
                            type="text"
                            className="input-pill form-control"
                            placeholder="KEYWORDS / TAGS"
                            value={keywordInput}
                            onChange={handleKeywordInput}
                            onKeyPress={handleKeywordKeyPress}
                            readOnly={isQaFormReadOnly}
                            disabled={isQaFormReadOnly}
                          />
                          <button type="button" className="btn-add" onClick={addKeyword} disabled={isQaFormReadOnly}>+ Add</button>
                        </div>
                        {scopeLogistics.keywords_tags.length > 0 && (
                          <div className="tags mt-2">
                            {scopeLogistics.keywords_tags.map((keyword, index) => (
                              <span key={index} className="tag">
                                {keyword}
                                <button
                                  type="button"
                                  className="tag-x"
                                  onClick={() => removeKeyword(keyword)}
                                  disabled={isQaFormReadOnly}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  </section>
                  
                  {/* INSPECTION SCHEDULE and TECHNICAL SPECIFICATIONS - Side by Side */}
                  <div className="row g-3">
                    <div className="col-12 col-md-6 d-flex">
                      <section className="sp-section w-100">
                        <div className="sp-subcard h-100">
                          <h6 className="sp-section-title">INSPECTION SCHEDULE</h6>
                          <p className="text-muted small mb-2">Timing requirements</p>
                          
                          <div className="row g-2">
                            <div className="col-12 col-md-6 mb-2">
                              <label className="d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#495057' }}>PREFERRED START DATE <span className="text-danger">*</span></label>
                              <input
                                type="date"
                                className="input-pill"
                                placeholder="dd-mm-yyyy"
                                value={scopeLogistics.preferred_start_date}
                                onChange={(e) => setScopeLogistics(prev => ({ ...prev, preferred_start_date: e.target.value }))}
                                required
                              />
                            </div>
                          
                            <div className="col-12 col-md-6 mb-2">
                              <label className="d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600', color: '#495057' }}>PREFERRED END DATE <span className="text-danger">*</span></label>
                              <input
                                type="date"
                                className="input-pill"
                                placeholder="dd-mm-yyyy"
                                value={scopeLogistics.preferred_end_date}
                                onChange={(e) => setScopeLogistics(prev => ({ ...prev, preferred_end_date: e.target.value }))}
                                required
                              />
                            </div>
                          
                            <div className="col-12 col-md-6 mb-2">
                              <Select
                                options={frequencyOptions}
                                value={frequencyOptions.find(opt => opt.value === scopeLogistics.frequency)}
                                onChange={(option) => setScopeLogistics(prev => ({ ...prev, frequency: option ? option.value : '' }))}
                                placeholder="FREQUENCY"
                                isDisabled={isQaFormReadOnly}
                                className="input-pill-select"
                                classNamePrefix="react-select"
                              />
                            </div>
                          
                            <div className="col-12 col-md-6 mb-2">
                              <div className="input-pill d-flex align-items-center gap-2" style={{ padding: '0.5rem 1rem', cursor: isQaFormReadOnly ? 'default' : 'pointer', minHeight: '38px' }} onClick={() => { if (!isQaFormReadOnly) setScopeLogistics(prev => ({ ...prev, flexible_dates: !prev.flexible_dates })); }}>
                                <input
                                  type="checkbox"
                                  checked={scopeLogistics.flexible_dates}
                                  onChange={(e) => setScopeLogistics(prev => ({ ...prev, flexible_dates: e.target.checked }))}
                                  disabled={isQaFormReadOnly}
                                  style={{ cursor: isQaFormReadOnly ? 'default' : 'pointer', margin: 0 }}
                                />
                                <span style={{ color: '#495057', userSelect: 'none' }}>FLEXIBLE DATES? ± {scopeLogistics.flexible_days} DAYS</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                    
                    <div className="col-12 col-md-6 d-flex">
                      <section className="sp-section w-100">
                    <div className="sp-subcard h-100">
                      <h6 className="sp-section-title">TECHNICAL SPECIFICATIONS</h6>
                      <p className="text-muted small mb-2">Product standards</p>
                      
                      <div className="row g-2">
                      <div className="col-12 col-md-6 mb-2">
                        <Select
                          options={samplingMethodOptions}
                          value={samplingMethodOptions.find(opt => opt.value === scopeLogistics.sampling_method)}
                          onChange={(option) => setScopeLogistics(prev => ({ ...prev, sampling_method: option ? option.value : '' }))}
                          placeholder="SAMPLING METHOD *"
                          isClearable
                          isDisabled={isQaFormReadOnly}
                          className="input-pill-select"
                          classNamePrefix="react-select"
                        />
                      </div>
                      
                      <div className="col-12 col-md-6 mb-2">
                        <input
                          type="number"
                          className="input-pill"
                          placeholder="LOT QUANTITY *"
                          value={scopeLogistics.lot_quantity}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, lot_quantity: e.target.value }))}
                          min="0"
                          step="1"
                        />
                      </div>
                      
                      <div className="col-12 mb-2">
                        <textarea
                          className="input-area"
                          placeholder="ACCEPTANCE CRITERIA"
                          value={scopeLogistics.acceptance_criteria}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, acceptance_criteria: e.target.value }))}
                          rows="2"
                        />
                      </div>
                    </div>
                    </div>
                      </section>
                    </div>
                  </div>
                  
                  {/* SERVICE LOCATION & LOGISTICS */}
                  <section className="sp-section mt-4">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">SERVICE LOCATION & LOGISTICS</h6>
                      <p className="text-muted small mb-2">Facility information</p>
                      
                      <div className="row g-2">
                      <div className="col-12 col-md-6 mb-2">
                        <input
                          type="text"
                          className="input-pill"
                          placeholder="ADDRESS LINE 1 *"
                          value={scopeLogistics.address_line_1}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, address_line_1: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 col-md-6 mb-2">
                        <input
                          type="text"
                          className="input-pill"
                          placeholder="ADDRESS LINE 2"
                          value={scopeLogistics.address_line_2}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, address_line_2: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <Select
                          options={countryOptions}
                          value={countryOptions.find(opt => opt.value === scopeLogistics.country)}
                          onChange={handleCountryChange}
                          placeholder="COUNTRY *"
                          isClearable
                          isDisabled={isQaFormReadOnly}
                          className="input-pill-select"
                          classNamePrefix="react-select"
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <Select
                          options={stateOptions}
                          value={stateOptions.find(opt => opt.value === scopeLogistics.state)}
                          onChange={handleStateChange}
                          placeholder="STATE *"
                          isClearable
                          isDisabled={isQaFormReadOnly || !scopeLogistics.country}
                          className="input-pill-select"
                          classNamePrefix="react-select"
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <Select
                          options={cityOptions}
                          value={cityOptions.find(opt => opt.value === scopeLogistics.city)}
                          onChange={(option) => setScopeLogistics(prev => ({ ...prev, city: option ? option.value : '' }))}
                          placeholder="CITY *"
                          isClearable
                          isDisabled={isQaFormReadOnly || !scopeLogistics.state}
                          className="input-pill-select"
                          classNamePrefix="react-select"
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <input
                          type="text"
                          className="input-pill"
                          placeholder="PINCODE *"
                          value={scopeLogistics.pincode}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, pincode: e.target.value }))}
                        />
                      </div>
                    </div>
                    </div>
                  </section>
                  
                  {/* ON-SITE COORDINATION */}
                  <section className="sp-section">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">ON-SITE COORDINATION</h6>
                      <p className="text-muted small mb-2">Communication & Access</p>
                      
                      <div className="row g-2">
                      <div className="col-12 col-md-3 mb-2">
                        <input
                          type="text"
                          className="input-pill"
                          placeholder="CONTACT NAME *"
                          value={scopeLogistics.contact_name}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, contact_name: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <input
                          type="text"
                          className="input-pill"
                          placeholder="DESIGNATION"
                          value={scopeLogistics.designation}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, designation: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <input
                          type="email"
                          className="input-pill"
                          placeholder="EMAIL *"
                          value={scopeLogistics.email}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, email: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 col-md-3 mb-2">
                        <input
                          type="tel"
                          className="input-pill"
                          placeholder="PHONE"
                          value={scopeLogistics.phone}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, phone: e.target.value }))}
                        />
                      </div>
                      
                      <div className="col-12 mb-2">
                        <textarea
                          className="input-area"
                          placeholder="SECURITY & PPE REQUIREMENTS"
                          value={scopeLogistics.security_ppe_requirements}
                          onChange={(e) => setScopeLogistics(prev => ({ ...prev, security_ppe_requirements: e.target.value }))}
                          rows="4"
                        />
                      </div>
                    </div>
                    </div>
                  </section>
                  
                  {/* REQUIREMENT DOCUMENTS */}
                  <section className="sp-section">
                    <div className="sp-subcard">
                      <h6 className="sp-section-title">REQUIREMENT DOCUMENTS</h6>
                      
                      <div className="row g-2">
                        <div className="col-12 col-md-6 mb-2">
                          <textarea
                            className="input-area"
                            placeholder="DETAILED INSPECTION INSTRUCTIONS *"
                            value={scopeLogistics.detailed_inspection_instructions}
                            onChange={(e) => setScopeLogistics(prev => ({ ...prev, detailed_inspection_instructions: e.target.value }))}
                            rows="3"
                          />
                        </div>
                        
                        <div className="col-12 col-md-6 mb-2">
                          <textarea
                            className="input-area"
                            placeholder="INDUSTRY STANDARDS (ISO/CE)"
                            value={scopeLogistics.industry_standards}
                            onChange={(e) => setScopeLogistics(prev => ({ ...prev, industry_standards: e.target.value }))}
                            rows="3"
                          />
                        </div>
                        
                        <div className="col-12 mb-2">
                          <label className="file-box d-block p-2 border rounded" style={{ cursor: isQaFormReadOnly ? 'default' : 'pointer', opacity: isQaFormReadOnly ? 0.7 : 1 }} onClick={() => { if (!isQaFormReadOnly) technicalDrawingsRef.current?.click(); }}>
                            <div className="file-content d-flex align-items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="17 8 12 3 7 8"></polyline>
                                <line x1="12" y1="3" x2="12" y2="15"></line>
                              </svg>
                              <span>TECHNICAL DRAWINGS / SOPS</span>
                            </div>
                            <div className="small text-muted mt-1">PDF, JPG (Max 10MB)</div>
                            <input 
                              type="file" 
                              style={{ display: 'none' }} 
                              ref={technicalDrawingsRef}
                              onChange={handleTechnicalDrawingsUpload}
                              accept=".pdf,.jpg,.jpeg,.png"
                              multiple
                            />
                            {scopeLogistics.technical_drawings_sops.length > 0 && (
                              <div className="mt-2">
                                {scopeLogistics.technical_drawings_sops.map((file, index) => (
                                  <div key={index} className="d-flex align-items-center justify-content-between mb-1 small">
                                    <span className="text-success">✓ {file.split('/').pop()}</span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-link text-danger p-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setScopeLogistics(prev => ({
                                          ...prev,
                                          technical_drawings_sops: prev.technical_drawings_sops.filter((_, i) => i !== index)
                                        }));
                                      }}
                                      disabled={isQaFormReadOnly}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
                </fieldset>
              <div className="container-fluid">
              {/* Tab 1 Actions */}
              <div className="d-flex justify-content-end gap-3 mt-4 mb-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleSaveDraft('1')}
                  disabled={saving || isQaFormReadOnly}
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '120px', padding: '12px 24px' }}
                >
                  {saving ? 'Saving...' : 'SAVE AS DRAFT'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    handleSaveDraft('1');
                    setActiveTab('2');
                  }}
                  disabled={saving || isQaFormReadOnly}
                  style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '120px', padding: '12px 24px' }}
                >
                  {saving ? 'Saving...' : 'NEXT'}
                </button>
              </div>
            </div>
          </TabPane>
          
          <TabPane tab="2. COMPLIANCE & COMMERCIALS" key="2">
            <fieldset
              disabled={isQaFormReadOnly}
              className="bqa-readonly-fieldset"
              style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}
            >
            <div className="container-fluid">
              {/* QUALITY & COMPLIANCE */}
              <section className="sp-section">
                <div className="sp-subcard">
                  <h6 className="sp-section-title">QUALITY & COMPLIANCE</h6>
                  <p className="text-muted small mb-2">Certifications and standards</p>
                  
                  <div className="row g-2">
                    <div className="col-12 col-md-6 mb-2">
                      <Select
                        options={activeQMSOptions}
                        value={activeQMSOptions.find(opt => opt.value === complianceCommercials.active_qms)}
                        onChange={(option) => setComplianceCommercials(prev => ({ ...prev, active_qms: option ? option.value : '' }))}
                        placeholder="ACTIVE QMS"
                        isClearable
                        isLoading={loadingQualityManagement}
                        isDisabled={isQaFormReadOnly}
                        className="input-pill-select"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <input
                        type="text"
                        className="input-pill"
                        placeholder="SYSTEM COMMENTS"
                        value={complianceCommercials.system_comments}
                        onChange={(e) => setComplianceCommercials(prev => ({ ...prev, system_comments: e.target.value }))}
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <input
                        type="text"
                        className="input-pill"
                        placeholder="INSURANCE POLICY NUMBER"
                        value={complianceCommercials.insurance_policy_number}
                        onChange={(e) => setComplianceCommercials(prev => ({ ...prev, insurance_policy_number: e.target.value }))}
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <label className="file-box d-block p-2 border rounded" style={{ cursor: isQaFormReadOnly ? 'default' : 'pointer', opacity: isQaFormReadOnly ? 0.7 : 1 }} onClick={() => { if (!isQaFormReadOnly) proofOfInsuranceRef.current?.click(); }}>
                        <div className="file-content d-flex align-items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="17 8 12 3 7 8"></polyline>
                            <line x1="12" y1="3" x2="12" y2="15"></line>
                          </svg>
                          <span>PROOF OF INSURANCE</span>
                        </div>
                        <div className="small text-muted mt-1">PDF, JPG (Max 10MB)</div>
                        <input 
                          type="file" 
                          style={{ display: 'none' }} 
                          ref={proofOfInsuranceRef}
                          onChange={handleProofOfInsuranceUpload}
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                        />
                        {complianceCommercials.proof_of_insurance.length > 0 && (
                          <div className="mt-2">
                            {complianceCommercials.proof_of_insurance.map((file, index) => (
                              <div key={index} className="d-flex align-items-center justify-content-between mb-1 small">
                                <span className="text-success">✓ {file.split('/').pop()}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setComplianceCommercials(prev => ({
                                      ...prev,
                                      proof_of_insurance: prev.proof_of_insurance.filter((_, i) => i !== index)
                                    }));
                                  }}
                                  disabled={isQaFormReadOnly}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </label>
                    </div>
                    
                    <div className="col-12 mb-2">
                      <textarea
                        className="input-area"
                        placeholder="COMPLIANCE HISTORY (PREVIOUS AUDITS)"
                        value={complianceCommercials.compliance_history}
                        onChange={(e) => setComplianceCommercials(prev => ({ ...prev, compliance_history: e.target.value }))}
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </section>
              
              {/* RISK ASSESSMENT */}
              <section className="sp-section">
                <div className="sp-subcard">
                  <h6 className="sp-section-title">RISK ASSESSMENT</h6>
                  <p className="text-muted small mb-2">Operational safety & mitigation</p>
                  
                  <div className="row g-2">
                    <div className="col-12 mb-2">
                      <label className="d-block mb-1" style={{ fontSize: '0.875rem', fontWeight: '600', color: '#495057' }}>IDENTIFIED RISKS?</label>
                      <div className="d-flex gap-3">
                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="identified_risks"
                            checked={!complianceCommercials.identified_risks}
                            onChange={() => setComplianceCommercials(prev => ({ ...prev, identified_risks: false, risk_description: '' }))}
                            disabled={isQaFormReadOnly}
                          />
                          <span>No</span>
                        </label>
                        <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="identified_risks"
                            checked={complianceCommercials.identified_risks}
                            onChange={() => setComplianceCommercials(prev => ({ ...prev, identified_risks: true, contingency_mitigation: '' }))}
                            disabled={isQaFormReadOnly}
                          />
                          <span>Yes</span>
                        </label>
                      </div>
                    </div>
                    
                    {complianceCommercials.identified_risks && (
                      <div className="col-12 mb-2">
                        <textarea
                          className="input-area"
                          placeholder="RISK DESCRIPTION"
                          value={complianceCommercials.risk_description}
                          onChange={(e) => setComplianceCommercials(prev => ({ ...prev, risk_description: e.target.value }))}
                          rows="3"
                        />
                      </div>
                    )}
                    
                    {!complianceCommercials.identified_risks && (
                      <div className="col-12 mb-2">
                        <textarea
                          className="input-area"
                          placeholder="CONTINGENCY & MITIGATION"
                          value={complianceCommercials.contingency_mitigation}
                          onChange={(e) => setComplianceCommercials(prev => ({ ...prev, contingency_mitigation: e.target.value }))}
                          rows="3"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </section>
              
              {/* COMMERCIAL & REPORTING */}
              <section className="sp-section">
                <div className="sp-subcard">
                  <h6 className="sp-section-title">COMMERCIAL & REPORTING</h6>
                  <p className="text-muted small mb-2">Budgeting and output requirements</p>
                  
                  <div className="row g-2">
                    <div className="col-12 col-md-6 mb-2">
                      <Select
                        options={budgetRangeOptions}
                        value={budgetRangeOptions.find(opt => opt.value === complianceCommercials.budget_range)}
                        onChange={(option) => setComplianceCommercials(prev => ({ ...prev, budget_range: option ? option.value : '' }))}
                        placeholder="BUDGET RANGE *"
                        isClearable
                        isDisabled={isQaFormReadOnly}
                        className="input-pill-select"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <Select
                        options={currencyOptions}
                        value={currencyOptions.find(opt => opt.value === complianceCommercials.currency || opt.currencyCode === complianceCommercials.currency)}
                        onChange={(option) => setComplianceCommercials(prev => ({ ...prev, currency: option ? (option.currencyCode || option.value) : '' }))}
                        placeholder="CURRENCY *"
                        isClearable
                        isDisabled={isQaFormReadOnly}
                        className="input-pill-select"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <input
                        type="date"
                        className="input-pill"
                        placeholder="dd-mm-yyyy"
                        value={complianceCommercials.bid_deadline}
                        onChange={(e) => setComplianceCommercials(prev => ({ ...prev, bid_deadline: e.target.value }))}
                        required
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <Select
                        options={reportFormatOptions}
                        value={reportFormatOptions.find(opt => opt.value === complianceCommercials.report_format)}
                        onChange={(option) => setComplianceCommercials(prev => ({ ...prev, report_format: option ? option.value : '' }))}
                        placeholder="REPORT FORMAT *"
                        isClearable
                        isDisabled={isQaFormReadOnly}
                        className="input-pill-select"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 mb-2">
                      <Select
                        options={maxTATOptions}
                        value={maxTATOptions.find(opt => opt.value === complianceCommercials.max_tat)}
                        onChange={(option) => setComplianceCommercials(prev => ({ ...prev, max_tat: option ? option.value : '' }))}
                        placeholder="MAX TAT *"
                        isClearable
                        isDisabled={isQaFormReadOnly}
                        className="input-pill-select"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="col-12 mb-2">
                      <textarea
                        className="input-area"
                        placeholder="REPORTING NOTES"
                        value={complianceCommercials.reporting_notes}
                        onChange={(e) => setComplianceCommercials(prev => ({ ...prev, reporting_notes: e.target.value }))}
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </section>
              
              {/* Tab 2 Actions */}
              <div className="d-flex justify-content-between gap-3 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveTab('1')}
                  style={{ minWidth: '120px', padding: '12px 24px' }}
                >
                  BACK
                </button>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleSaveDraft('2')}
                    disabled={saving || isQaFormReadOnly}
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '120px', padding: '12px 24px' }}
                  >
                    {saving ? 'Saving...' : 'SAVE AS DRAFT'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleBroadcastRFQ}
                    disabled={saving || isQaFormReadOnly}
                    style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35', minWidth: '120px', padding: '12px 24px' }}
                  >
                    {saving ? 'Processing...' : 'SUBMIT REQUEST'}
                  </button>
                </div>
              </div>
            </div>
            </fieldset>
          </TabPane>
        </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
